import _ from 'lodash';
import semver from 'semver';

import { VersionInfo, MigrationsBySemVerRange } from './types';
import { MIN_VERSION } from './constants';

/** Order version objects in descending order */
export const orderVersionsBySemVer = (versions: VersionInfo[] = []) => {
  return [...versions]
    /* TODO: Why do we have an "undefined" versions? */
    .filter(item => item.version)
    .sort((a, b) => semver.compare(b.version, a.version));
};

/**
 * What migrations are needed in a sequential order for given app version.
 * Example:
 *  - Version: "2.5.0"
 *  - Range: { ">=1.0.0": "01-A", ">=2.0.0": "02-B", ">=3.0.0": "03-C" }
 *  => ["01-A", "02-B"]
 */
export const getMigrationsByAppVersion = (args: {
  version?: string;
  migrationsByRange: MigrationsBySemVerRange;
}) => {
  if (!args.version) {
    return [];
  }

  const entries = Object.entries(args.migrationsByRange);
  const migrations = [];

  for (const [range, migration] of entries) {
    if (semver.satisfies(args.version, range, { includePrerelease: true })) {
      migrations.unshift(migration);
    }
  }

  return migrations;
};

/** Check if an update from active version to new version will require data migrations */
export const needsDataUpdate = (args: {
  vActive?: string;
  vTarget?: string;
  migrationsByRange: MigrationsBySemVerRange;
}) => {
  const { vActive, vTarget, migrationsByRange } = args;
  const mActive = getMigrationsByAppVersion({
    version: vActive,
    migrationsByRange,
  });

  const mTarget = getMigrationsByAppVersion({
    version: vTarget,
    migrationsByRange,
  });

  return _.difference(mTarget, mActive).length !== 0;
};

/**
 * Determine max HITL version that we can use with the current DB migration.
 * Return only non-RC versions.
 * Example:
 *  - Migration: "02-B"
 *  - Ranges: { ">=1.0.0": "01-A", ">=2.0.0": "02-B", ">=3.0.0": "03-C" }
 *  - Versions: ["1.0.0", "2.0.0", "2.5.0", "2.6.0-rc.1" "3.0.0"]
 *  => "2.5.0"
 */
export const getMaxAppVersionByMigration = (args: {
  migration: string;
  versions: VersionInfo[];
  migrationsByRange: MigrationsBySemVerRange;
}) => {
  const versions = orderVersionsBySemVer(args.versions);
  const migrations = Object.values(args.migrationsByRange);
  const index = migrations.indexOf(args.migration);

  /* If account has the latest HITL database version */
  if (index === 0) {
    return versions.find(version => !version.prerelease.length)?.version || '';
  }

  const ranges = _.invert(args.migrationsByRange);
  const minRange = ranges[args.migration];
  const maxRange = ranges[migrations[index - 1]];

  for (const version of versions) {
    const isRCVersion = !!version.prerelease?.length;
    const isMoreOrEqualMinRange = semver.satisfies(version.version, minRange);
    const isLessMaxRange = !semver.satisfies(version.version, maxRange);

    if (!isRCVersion && isMoreOrEqualMinRange && isLessMaxRange) {
      return version.version;
    }
  }

  return '';
};

/**
 * Example:
 *  - Migrations: ["01-A", "02-B"]
 *  - Range: { ">=1.0.0": "01-A", ">=2.0.0": "02-B", ">=3.0.0": "03-C" }
 *  => "2.0.0"
 */
export const getMinAvailableVersion = (args: {
  migrations: string[];
  migrationsByRange: MigrationsBySemVerRange;
}) => {
  if (!args.migrations.length) {
    return MIN_VERSION;
  }

  const update = args.migrations.at(-1)!;
  const ranges = _.invert(args.migrationsByRange);

  const minVersion = semver.minVersion(ranges[update]);
  return minVersion ? minVersion.version : MIN_VERSION;
};
